import React, { useMemo, useCallback } from 'react'
import BedbankHotelTile from './BedbankHotelTile'
import LeHotelTile from './LeHotelTile'
import Group from 'components/utils/Group'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Pane from 'components/Common/Pane'
import Divider from 'LuxKit/Divider'
import cn from 'clsx'
import {
  isFlightItem,
  isBedbankItem,
  isInstantBookingLEHotelItem,
} from 'lib/checkout/checkoutUtils'
import FlightCartReviewTile from '../FlightCartReviewTile'
import DeletedCartReviewTile from '../DeletedCartReviewTile'
import { useUserCartItemState } from 'hooks/UserCart/useUserCartItemState'
import Sheet from 'components/utils/Sheet'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import useOffer from 'hooks/Offers/useOffer'
import { formatDynamicDateRange } from 'lib/datetime/dateUtils'
import TextButton from 'LuxKit/Button/TextButton'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import { useAppDispatch } from 'hooks/reduxHooks'
import { userCartItemsDelete, userCartItemsSaveForLater } from 'actions/UserCartActions'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import IconButton from 'LuxKit/Button/IconButton'

const FlightHotelBundleCartReviewGroupContainer = styled(Pane)`
  background-color: ${(props) => props.theme.palette.neutral.default.eight};
  border: none;
  ${mediaQueryUp.tablet} {
    border: 1px solid ${(props) => props.theme.palette.neutral.default.five};
  }
  &.is-condensed {
    border: none;
  }
`
const DodgyButton = styled(IconButton)`
  border: none;
`

const FlightHotelBundleCartReviewGroupContent = styled(Group)`
  position: relative;
`

const StyledFlightCartReviewTile = styled(FlightCartReviewTile)`
  border: none;
`

interface Props {
  items: Array<
    | App.Checkout.FlightItem
    | App.Checkout.BedbankHotelItem
    | App.Checkout.InstantBookingLEHotelItem
  >;
  tileVariant?: 'default' | 'condensed';
  banner?: React.ReactNode;
}

function FlightHotelBundleCartReviewGroup({
  items,
  tileVariant = 'default',
  banner,
}: Props) {
  const dispatch = useAppDispatch()

  const { flightItems, accommodationItems } = useMemo(() => {
    const flightItems: Array<App.Checkout.FlightItem> = []
    const accommodationItems: Array<
      App.Checkout.BedbankHotelItem | App.Checkout.InstantBookingLEHotelItem
    > = []

    items.forEach((item) => {
      if (isFlightItem(item)) {
        flightItems.push(item)
      } else {
        accommodationItems.push(item)
      }
    })

    return { flightItems, accommodationItems }
  }, [items])
  const hotelItem = accommodationItems[0]
  const [offer] = useOffer<App.HotelOffer | App.BedbankOffer>(hotelItem.offerId)

  // Get item states for the first items to check deletion status
  const hotelItemState = useUserCartItemState(accommodationItems[0]?.itemId)
  const flightItemState = useUserCartItemState(flightItems[0]?.itemId)

  // Check if all items in the bundle are deleted
  const areAllItemsDeleted = hotelItemState.deleted && flightItemState.deleted

  // Check if all items are saved for later
  const areAllItemsSavedForLater = hotelItemState.saveForLater && flightItemState.saveForLater

  // Get bundle title from the hotel item
  const bundleTitle = useMemo(() => {
    const hotelItem = accommodationItems[0]
    if (!hotelItem) return 'Flight + Hotel Bundle'

    const hotelName = isBedbankItem(hotelItem)
      ? hotelItem.offerId
      : isInstantBookingLEHotelItem(hotelItem)
        ? hotelItem.offerId
        : 'Hotel'

    return `${hotelName} + Flight Bundle`
  }, [accommodationItems])

  // Get all item IDs from the bundle
  const allItemIds = useMemo(() => items.map((item) => item.itemId), [items])

  // Handlers for bundle-level actions
  const handleSaveForLater = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    dispatch(userCartItemsSaveForLater(allItemIds))
  }, [allItemIds, dispatch])

  const handleDelete = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    dispatch(userCartItemsDelete(allItemIds))
  }, [allItemIds, dispatch])

  // Create filtered items arrays for each tile type
  const bedbankBundleItems = useMemo(() => {
    const bedbankHotel = accommodationItems.find((item) => isBedbankItem(item))
    if (!bedbankHotel) return []
    return [...flightItems, bedbankHotel]
  }, [accommodationItems, flightItems])

  const leHotelBundleItems = useMemo(() => {
    const leHotel = accommodationItems.find((item) =>
      isInstantBookingLEHotelItem(item)
    )
    if (!leHotel) return []
    return [...flightItems, leHotel]
  }, [accommodationItems, flightItems])

  const isCondensed = tileVariant === 'condensed'

  // If all items are deleted, show deleted tile instead of the tiles
  if (areAllItemsDeleted) {
    return (
      <FlightHotelBundleCartReviewGroupContainer
        className={cn({ 'is-condensed': isCondensed })}
      >
        {banner}
        <DeletedCartReviewTile title={bundleTitle} itemIds={allItemIds} />
      </FlightHotelBundleCartReviewGroupContainer>
    )
  }

  const isMultiRoom = accommodationItems.length > 1
  const hotelName = offer?.property.name || offer?.name || 'Hotel'
  const dateRange = formatDynamicDateRange(hotelItem.checkIn, hotelItem.checkOut)
  const summary = dateRange ? `${dateRange} · ${accommodationItems.length} rooms · ${hotelItem.duration} nights` : undefined

  const disableButtons = areAllItemsDeleted || hotelItemState.deleted || flightItemState.deleted

  const buttons = (
    <>

      {!areAllItemsSavedForLater && !isCondensed && (
        <TextButton
          kind="secondary"
          size="small"
          shape="round"
          disabled={disableButtons}
          onClick={handleSaveForLater}

        >
          Save all for later
        </TextButton>
      )}
      {areAllItemsSavedForLater && !isCondensed && (
        <TextButton
          kind="primary"
          size="small"
          shape="round"
          disabled={disableButtons}
          onClick={handleSaveForLater}

        >
          Add all
        </TextButton>
      )}

      {!isCondensed && <TextButton
        kind="secondary"
        size="small"
        shape="round"
        disabled={disableButtons}
        startIcon={<LineTrashAltIcon />}
        onClick={handleDelete}
      >
        Delete all
      </TextButton>}
      {isCondensed && <DodgyButton
        kind="secondary"
        horizontalOutdent="end"
        verticalOutdent="start"
        onClick={handleDelete}
      >
        <LineTrashAltIcon />
      </DodgyButton>}
    </>
  )

  return (
    <FlightHotelBundleCartReviewGroupContainer
      className={cn({ 'is-condensed': isCondensed })}
    >
      {banner}
      <FlightHotelBundleCartReviewGroupContent direction="vertical">
        {isMultiRoom && (
          <Sheet
            background="neutral-eight"
            border="transparent"
            borderRadius="S"
            padding={isCondensed ? 16 : 20}
          >
            <Group direction="horizontal" horizontalAlign="space-between">
              <Group direction="vertical" gap={2}>
                <Heading variant="heading6">{hotelName}</Heading>
                {summary && <BodyText variant="medium">{summary}</BodyText>}
              </Group>

              <Group direction="horizontal" gap={8}>
                {buttons}
              </Group>

            </Group>
          </Sheet>
        )}
        {accommodationItems.map((accommodationItem) => (
          <React.Fragment key={accommodationItem.itemId}>
            <Divider kind="primary" direction="horizontal" />
            {isBedbankItem(accommodationItem) && (
              <BedbankHotelTile
                items={bedbankBundleItems}
                tileVariant={tileVariant}
                hideTitleAndDates={isMultiRoom}
                hideButtons={isMultiRoom}
              />
            )}
            {isInstantBookingLEHotelItem(accommodationItem) && (
              <LeHotelTile
                items={leHotelBundleItems}
                tileVariant={tileVariant}
                hideTitleAndDates={isMultiRoom}
                hideButtons={isMultiRoom}
              />
            )}
          </React.Fragment>
        ))}
        {flightItems.map((flightItem) => (
          <React.Fragment key={flightItem.itemId}>
            <Divider kind="primary" direction="horizontal" />
            <StyledFlightCartReviewTile
              item={flightItem}
              tileVariant={tileVariant}
              hideActionButtons
              hidePrices
              hidePassengerCount
              hideLoyaltyPoints
            />
          </React.Fragment>
        ))}
        {isMultiRoom && (
          <CSSBreakpoint max="mobile">
            <Divider kind="primary" direction="horizontal" />
            <Sheet
              background="neutral-eight"
              border="transparent"
              borderRadius="S"
              padding="16 16"
            >
              <Group direction="horizontal" gap={8}>
                {buttons}
              </Group>
            </Sheet>
          </CSSBreakpoint>
        )}
      </FlightHotelBundleCartReviewGroupContent>
    </FlightHotelBundleCartReviewGroupContainer>
  )
}

export default FlightHotelBundleCartReviewGroup
